// moduleFileBridge.js — capability-scoped file upload for workspace MODULES
// (manifest v2). The file-plane sibling of moduleDataBridge/moduleAiBridge:
// when a module ref carries `capabilities.files`, the loader injects
// `moduleFileUpload(file, opts)` so a module can store a real blob (e.g. a PDF
// CV) via `el.call` — never raw fetch / a browser upload (the publish-time
// module audit forbids those). It reuses the existing user-scoped file service
// (`sdk.file.uploadDocument` → POST /core/files/upload → GCS + a Mongo File
// doc) and returns a public download URL to persist on a record.
//
// Returns a plain result object `{ ok, id, url, name, size } | { ok:false,
// error, status? }` (never throws) so a module upload UI degrades gracefully.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

// What the workspace file plane accepts, MIRRORING the server's own filter
// (server/src/core/middleware/upload.js — ALLOWED_DOCUMENT_TYPES +
// ALLOWED_WORKSPACE_TYPES). The server is the real authority; this list exists
// only so a module gets an instant, accurate rejection instead of a round-trip
// 4xx, which means it has to stay in step with it.
//
// It previously mirrored the SDK's `uploadDocument` helper — pdf/txt/doc/docx
// and nothing else — which had drifted well behind the server. The visible
// cost: the server had already been taught that a `.excalidraw` is
// `application/vnd.excalidraw+json` and allowed, and yet every board save and
// every 20s autosave was rejected in the browser before a request was made.
// Any module storing markdown or CSV was equally stuck.
const DEFAULT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'text/markdown',
  'text/x-markdown',
  // Formats a workspace SURFACE opens, as opposed to ones a viewer renders.
  'application/vnd.excalidraw+json'
]

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024

// READ side of the files grant. A module could upload but never read back —
// `moduleData` gates its entity list to `records` only, so a files read came
// back as "entity \"files\" is not in this module's capability grant" and the
// board's Open-from-Files was dead on arrival.
//
// Both are gated on the SAME `capabilities.files` grant as the upload, and both
// are scoped to the module's OWN uploads (the `module: 'workspaceModule'`
// stamp written at upload time) — a files grant is not a licence to read the
// user's whole drive.
export const buildModuleFileListBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  return async function moduleFileList(opts = {}) {
    const options = opts || {}
    const sdk = getSdkFn ? getSdkFn() : null
    const fileSvc =
      sdk && (typeof sdk.getService === 'function' ? await sdk.getService('file') : sdk.file)
    if (!fileSvc || typeof fileSvc.listMyUploads !== 'function') {
      return { ok: false, error: 'File service unavailable.', files: [] }
    }
    try {
      const res = await fileSvc.listMyUploads({
        limit: Math.min(Number(options.limit) || 25, 100),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })
      const rows = Array.isArray(res) ? res : (res && (res.data || res.files)) || []
      const name = (f) => String((f && (f.originalName || f.filename || f.name)) || '')
      const files = rows
        // Own uploads only, and — when the caller asks — only the extension it
        // actually opens, so a module never has to sift the user's documents.
        .filter((f) => {
          const meta = (f && f.metadata) || {}
          if (meta.module && meta.module !== 'workspaceModule') return false
          if (options.kind && meta.kind && meta.kind !== options.kind) return false
          if (options.extension) {
            return name(f).toLowerCase().endsWith(String(options.extension).toLowerCase())
          }
          return true
        })
        .map((f) => ({
          id: f && (f._id || f.id),
          name: name(f),
          size: f && f.size,
          createdAt: f && (f.createdAt || f.created_at),
          // Carried so a read can go STRAIGHT to the stored object instead of
          // bouncing through the API's redirect (see moduleFileRead).
          url: (f && (f.storageUrl || f.url)) || ''
        }))
      return { ok: true, files }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not list files.', files: [] }
    }
  }
}

// Return a stored file's CONTENT as text. The download itself is done HERE, in
// the shell, on the module's behalf — that is the whole point of a capability
// bridge. Module code may not perform its own transport (the publish-time
// audit forbids fetch/XHR outright), and the URL comes from the SDK rather
// than being assembled by the caller, so a module cannot point this at an
// arbitrary host.
export const buildModuleFileReadBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  return async function moduleFileRead(ref) {
    // Accepts an id, or a row from `moduleFileList` (whose `url` points
    // straight at the stored object).
    const fileId = ref && typeof ref === 'object' ? ref.id : ref
    const directUrl = (ref && typeof ref === 'object' && ref.url) || ''
    if (!fileId && !directUrl) return { ok: false, error: 'A file id is required.' }
    const sdk = getSdkFn ? getSdkFn() : null
    const fileSvc =
      sdk && (typeof sdk.getService === 'function' ? await sdk.getService('file') : sdk.file)

    // AUTHENTICATED SDK download first. Module files are uploaded private, and
    // both unauthenticated paths fail for that exact reason — the public route
    // 404s (it only serves files published as public) and the raw GCS object
    // 403s (that bucket is not world-readable). Measured both. So the real read
    // is `GET /core/files/:id/download` behind requireAuth, which the SDK owns.
    if (fileId && fileSvc && typeof fileSvc.downloadFileContent === 'function') {
      try {
        const text = await fileSvc.downloadFileContent(fileId)
        if (typeof text === 'string' && text) return { ok: true, text }
      } catch (authErr) {
        /* fall through to the URL candidates below */
      }
    }

    // Fallbacks, for genuinely PUBLIC files (a published asset, an older
    // upload): direct object URL first, the API's public route second. Both are tried
    // because which one resolves is environment-shaped, and a reader that gives
    // up after the first works on dev and fails in production.
    const candidates = []
    if (directUrl) candidates.push(directUrl)
    if (fileId && fileSvc && typeof fileSvc.getFileUrl === 'function') {
      try {
        const u = fileSvc.getFileUrl(fileId)
        if (u && u !== directUrl) candidates.push(u)
      } catch (urlErr) {
        /* fall through to whatever else we have */
      }
    }
    if (!candidates.length) return { ok: false, error: 'That file has no readable URL.' }

    // `credentials: 'omit'` ON PURPOSE. These objects live in a PUBLIC bucket
    // (allUsers:objectViewer) whose CORS allows GET/HEAD only. Sending
    // credentials makes it a CREDENTIALED cross-origin read, which GCS will not
    // satisfy — the browser rejects it before any status exists, surfacing as a
    // bare "Failed to fetch" that reads like the file is missing. It is not;
    // the object is public and needs no credentials. (See the CORS note in
    // server StorageService.js: "the objects are public but fetch() from the
    // app is blocked, so in-browser readers fail while curl succeeds".)
    let lastErr = ''
    for (const url of candidates) {
      try {
        const res = await fetch(url, { credentials: 'omit' })
        if (res && res.ok) return { ok: true, text: await res.text() }
        lastErr = `Could not read the file (${res && res.status}).`
      } catch (e) {
        lastErr = (e && e.message) || 'Could not read the file.'
      }
    }

    // LAST RESORT — the object through the API. A public-bucket URL only
    // works where the BUCKET's CORS config lists the app's origin, and that
    // list covers the deployed hosts only: measured 2026-08-11, every
    // `*.localhost` dev origin gets no ACAO header, so BOTH candidates above
    // die with a bare "Failed to fetch" while curl serves the same object
    // 200. The API sits behind the same CORS surface as every other SDK
    // call, so `POST /core/storage/:bucket/download` (auth + workspace
    // prefix guard server-side) works wherever the app itself works. Only
    // desktop-plane objects qualify: their URL path is
    // `…/<physical-bucket>/<logicalBucket>/<workspaceId>/<file>`, and the
    // server rejects any path outside the caller's own workspace prefix.
    const wsDownload = _storageRefFromUrl(directUrl)
    if (wsDownload && sdk && typeof sdk.execute === 'function') {
      try {
        const text = await sdk.execute('workspaceProject.storage', 'download', wsDownload)
        if (typeof text === 'string' && text) return { ok: true, text }
      } catch (apiErr) {
        lastErr = (apiErr && apiErr.message) || lastErr
      }
    }
    return { ok: false, error: lastErr || 'Could not read the file.' }
  }
}

// Server-side text extraction (docx/pdf → plain text) for a stored file. The
// SAME auth-first pattern as moduleFileRead above — the module never fetches
// the bytes itself, it asks the shell, which asks the SDK
// (`GET /core/files/:id/extract-text`, requireAuth, reuses the same
// downloadFile read as the authenticated download path above). This is the
// module-side half of F16.2b (tickets/opus.md "BF-LATER"): a module that
// uploaded a docx/pdf (moduleFileUpload) can now pull real extracted text
// back out of it instead of only a raw byte round-trip.
export const buildModuleFileExtractTextBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  return async function moduleFileExtractText(ref) {
    const fileId = ref && typeof ref === 'object' ? ref.id : ref
    if (!fileId) return { ok: false, error: 'A file id is required.' }
    const sdk = getSdkFn ? getSdkFn() : null
    const fileSvc =
      sdk && (typeof sdk.getService === 'function' ? await sdk.getService('file') : sdk.file)
    if (!fileSvc || typeof fileSvc.extractFileText !== 'function') {
      return { ok: false, error: 'File service unavailable.' }
    }
    try {
      const result = await fileSvc.extractFileText(fileId)
      if (!result || typeof result.text !== 'string') {
        return { ok: false, error: 'That file could not be read as text.' }
      }
      return { ok: true, ...result }
    } catch (e) {
      // Server distinguishes unsupported type / too large / corrupt /
      // timed-out — the message is already user-facing (FileController.js
      // extractText), so it is passed through rather than genericized.
      return { ok: false, error: (e && e.message) || 'Could not extract text from that file.' }
    }
  }
}

// Parse a stored public-bucket object URL back into the `{ bucket, path }`
// the storage API takes. Recognizes the workspace logical buckets wherever
// they appear as a path segment (works for any physical-bucket host or name):
//   https://storage.googleapis.com/<physical>/workspace-files/<wsId>/<file>
//     → { bucket: 'workspace-files', path: '<wsId>/<file>' }
// Returns null for anything else — module-plane /core/files objects have
// their own authenticated route (downloadFileContent above).
const WORKSPACE_STORAGE_BUCKETS = ['workspace-files', 'chat-attachments']
export const _storageRefFromUrl = (url) => {
  if (!url) return null
  let segs
  try {
    segs = new URL(url).pathname.split('/').filter(Boolean)
  } catch (e) {
    return null
  }
  for (const bucket of WORKSPACE_STORAGE_BUCKETS) {
    const i = segs.indexOf(bucket)
    if (i >= 0 && i < segs.length - 1) {
      return { bucket, path: decodeURIComponent(segs.slice(i + 1).join('/')) }
    }
  }
  return null
}

export const buildModuleFileBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK, uploadLarge = null } = {}
) => {
  const cap = (capabilities && capabilities.files) || null
  // A PRIVATE upload used to come back with the PUBLIC download URL
  // (`fileSvc.getFileUrl`), which answers 404 for a private file — for
  // everyone, the uploader included (measured on dev 2026-10-23: TC task
  // assets were dead links; the CMS stores this url as its image src). The
  // direct path mints a LINK exactly for this, so a private upload goes
  // through it first. The multipart path below stays for public files and
  // as the fallback ONLY when the direct path is unavailable: an older API,
  // which the large bridge marks `unavailable` (concierge 2026-11-01). A
  // refusal is the answer. The direct path's workspace scope lets only
  // editor or above upload (server FileController.beginDirectUpload, 403
  // below that), and the multipart upload has no such floor: falling back on
  // a 403 stored the refused file as the member's personal file.
  const isPrivate = ((cap && cap.visibility) || 'private') !== 'public'
  let viaLink = typeof uploadLarge === 'function' ? uploadLarge : null
  // An OPERATOR may narrow or widen this per install via the stored
  // `capabilities.files.mimeTypes` on the server-side module ref. Note the
  // source: capabilities come from the SERVER REF ONLY, NEVER THE MANIFEST
  // (workspaceProjects.js:371) — an app cannot widen its own file grant by
  // declaring a field in config.js, and must not be able to. Not a trust
  // boundary either way; the server's upload filter is.
  const allowedTypes =
    Array.isArray(cap && cap.mimeTypes) && cap.mimeTypes.length ? cap.mimeTypes : DEFAULT_MIME_TYPES

  return async function moduleFileUpload(file, opts = {}) {
    const options = opts || {}
    if (!file || typeof file !== 'object' || typeof file.name !== 'string') {
      return { ok: false, error: 'A file is required.' }
    }
    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk) return { ok: false, error: 'File service unavailable.' }

    if (isPrivate && !(typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES)) {
      if (!viaLink) viaLink = buildModuleFileUploadLargeBridge(root, capabilities, { getSdkFn })
      const linked = await viaLink(file, { kind: options.kind || '' })
      if (linked && linked.ok) {
        return { ok: true, id: linked.id, url: linked.url, name: linked.name, size: linked.size }
      }
      if (linked && linked.cancelled) return linked
      // Not offered by this API (404 / 405 / 501, no workspace scope, an
      // older SDK): the multipart path below. Anything else (a 403 below
      // editor, a 413 over the tier, a failed transfer) is the answer.
      if (!(linked && linked.unavailable === true)) {
        return {
          ok: false,
          error: (linked && linked.error) || 'Upload failed.',
          ...(linked && typeof linked.status === 'number' ? { status: linked.status } : {})
        }
      }
    }

    // Services init async; reach via getService (a promise). sdk.file may also
    // be a direct property on some builds.
    let fileSvc = null
    try {
      fileSvc = typeof sdk.getService === 'function' ? await sdk.getService('file') : sdk.file
    } catch (e) {
      fileSvc = sdk.file || null
    }
    if (!fileSvc || typeof fileSvc.uploadDocument !== 'function') {
      return { ok: false, error: 'File service unavailable.' }
    }
    // `uploadDocument` SPREADS ITS OWN `allowedTypes` OVER the caller's, so
    // passing them to it does nothing — the declared list has to go through
    // the validating primitive directly.
    const upload =
      typeof fileSvc.uploadFileWithValidation === 'function'
        ? (f, o) => fileSvc.uploadFileWithValidation(f, o)
        : (f, o) => fileSvc.uploadDocument(f, o)

    const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || undefined

    try {
      // 20MB cap retained from the old document path. Reached by public
      // files and as the fallback for private ones (see the top of this
      // builder): a PRIVATE file's url from here does not open.
      const rec = await upload(file, {
        allowedTypes,
        maxSize: MAX_UPLOAD_BYTES,
        visibility: (cap && cap.visibility) || 'private',
        metadata: { workspaceId: wsId, module: 'workspaceModule', kind: options.kind || '' }
      })
      const id = rec && (rec._id || rec.id)
      const url =
        (typeof fileSvc.getFileUrl === 'function' && id && fileSvc.getFileUrl(id)) ||
        (rec && (rec.storageUrl || rec.url)) ||
        ''
      return {
        ok: true,
        id: id,
        url: url,
        name: rec && (rec.originalName || rec.filename),
        size: rec && rec.size
      }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Upload failed.' }
    }
  }
}

// LARGE uploads over the SAME files grant — `moduleFileUploadLarge(file, {
// onProgress, signal, kind })`. `moduleFileUpload` above is one multipart
// request through the API: capped at 20 MB here and by the ingress (~32 MB)
// anyway. Long-form media (a 3 GB edit delivered on a task) goes DIRECT to
// storage instead (server POST /core/files/direct):
//
//   1. open a session SCOPED TO THIS WORKSPACE — the server checks the member
//      holds editor or above there and applies the workspace's tier limit
//      (free 50 MB … enterprise 10 GB; a module's users are workspace
//      members, not members of the module's project, so a project scope would
//      refuse them) and mints a download LINK;
//   2. PUT the bytes to the resumable storage session in chunks (XHR, for
//      upload progress), resuming from what storage says it holds after a
//      failed chunk — a dropped connection costs one chunk, not the file;
//   3. complete — the server holds the object that arrived to the limit.
//
// The link (GET /core/files/link/:id/:token) answers with a short-lived signed
// storage URL, so the stored `url` downloads/plays for whoever the module
// shows it to without the bytes passing through the API (its other download
// routes read the whole object into memory). Never throws: `{ ok, id, url,
// name, size, mimeType } | { ok:false, error, cancelled?, status?,
// unavailable? }`. `unavailable: true` marks a direct path this API does not
// offer (an older SDK; /direct answers 404, 405 or 501; a session without the
// workspace scope): only then may a caller try another upload path. Every
// other failure is the answer, a 403 below editor included.
//
// Cancel: pass `signal` — an AbortSignal, or any `{ aborted,
// addEventListener('abort', fn) }` (a module need not reach for a global
// constructor). The session is aborted server-side, so nothing is left behind.

const LARGE_CHUNK_QUANTUM = 256 * 1024 // GCS: every chunk but the last is a multiple of this
const LARGE_DEFAULT_CHUNK_BYTES = 8 * 1024 * 1024
const LARGE_MAX_CHUNK_ATTEMPTS = 6
const LARGE_PROGRESS_INTERVAL_MS = 250
const LARGE_COMPLETE_ATTEMPTS = 4

// A browser reports some media under names the server's allowlist does not
// carry (server/src/core/middleware/upload.js ALL_ALLOWED_TYPES) — and reports
// nothing at all for some files. Map both onto the allowlisted name; the
// declared type becomes the stored object's Content-Type.
const LARGE_MIME_ALIASES = {
  'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/aac': 'audio/mp4',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
  'audio/mp3': 'audio/mpeg',
  'video/x-m4v': 'video/mp4',
  'application/x-zip': 'application/zip',
  'application/zip-compressed': 'application/zip',
  'application/x-zip-compressed': 'application/zip'
}
const LARGE_MIME_BY_EXT = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  aac: 'audio/mp4',
  ogg: 'audio/ogg',
  zip: 'application/zip',
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp'
}

export const _largeMimeOf = (file) => {
  const raw = String((file && file.type) || '')
    .toLowerCase()
    .split(';')[0]
    .trim()
  if (raw && raw !== 'application/octet-stream') return LARGE_MIME_ALIASES[raw] || raw
  const m = /\.([a-z0-9]+)$/i.exec(String((file && file.name) || ''))
  return (m && LARGE_MIME_BY_EXT[m[1].toLowerCase()]) || raw || ''
}

const _largeTypeAllowed = (mime, allowed) =>
  !!mime &&
  allowed.some((t) =>
    String(t).endsWith('/*') ? mime.indexOf(String(t).slice(0, -1)) === 0 : t === mime
  )

// `bytes=0-N` → N + 1 (the next byte storage wants). null when absent.
export const _largeNextOffset = (range) => {
  const m = /bytes=(\d+)-(\d+)/.exec(String(range || ''))
  return m ? Number(m[2]) + 1 : null
}

const _largeRetryable = (status) =>
  status === 0 || status === 408 || status === 429 || (status >= 500 && status < 600)

const _largeOnAbort = (signal, fn) => {
  if (!signal || typeof signal.addEventListener !== 'function') return () => {}
  signal.addEventListener('abort', fn)
  return () => {
    try {
      signal.removeEventListener && signal.removeEventListener('abort', fn)
    } catch (e) {
      /* a signal-like without remove is fine */
    }
  }
}

// One PUT to the storage session. Resolves `{ status, range }` — never rejects.
const _largePut = (xhrFactory, url, body, contentRange, onBytes, signal) =>
  new Promise((resolve) => {
    let xhr
    try {
      xhr = xhrFactory()
    } catch (e) {
      resolve({ status: 0, error: (e && e.message) || 'no transport' })
      return
    }
    let settled = false
    let detach = () => {}
    const done = (r) => {
      if (settled) return
      settled = true
      detach()
      resolve(r)
    }
    detach = _largeOnAbort(signal, () => {
      try {
        xhr.abort()
      } catch (e) {
        /* already finished */
      }
      done({ status: 0, aborted: true })
    })
    try {
      xhr.open('PUT', url, true)
      xhr.setRequestHeader('Content-Range', contentRange)
      if (xhr.upload && onBytes) {
        xhr.upload.onprogress = (e) => {
          if (e && typeof e.loaded === 'number') onBytes(e.loaded)
        }
      }
      xhr.onload = () => {
        // Only a 308 (storage holds part of the object) carries the Range
        // the resume reads. Read it from getAllResponseHeaders(), which lists
        // only the headers the bucket's CORS exposes: getResponseHeader on an
        // unexposed header logs "Refused to get unsafe header" on every PUT
        // (a console error per upload, seen on dev 2026-10-23). Unexposed →
        // null, as before.
        let range = null
        if (xhr.status === 308) {
          try {
            const all =
              typeof xhr.getAllResponseHeaders === 'function'
                ? String(xhr.getAllResponseHeaders() || '')
                : ''
            const m = /^range:\s*(.+)$/im.exec(all)
            range = m ? m[1].trim() : null
          } catch (e) {
            range = null
          }
        }
        done({ status: xhr.status, range })
      }
      xhr.onerror = () => done({ status: 0, error: 'network' })
      xhr.ontimeout = () => done({ status: 0, error: 'timeout' })
      xhr.onabort = () => done({ status: 0, aborted: true })
      xhr.send(body)
    } catch (e) {
      done({ status: 0, error: (e && e.message) || 'send failed' })
    }
  })

export const buildModuleFileUploadLargeBridge = (
  root,
  capabilities = {},
  {
    getSdkFn = shellGetSDK,
    xhrFactory = () => new XMLHttpRequest(),
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
    now = () => Date.now()
  } = {}
) => {
  const cap = (capabilities && capabilities.files) || null
  const allowedTypes =
    Array.isArray(cap && cap.mimeTypes) && cap.mimeTypes.length ? cap.mimeTypes : DEFAULT_MIME_TYPES

  return async function moduleFileUploadLarge(file, opts = {}) {
    const options = opts || {}
    const signal = options.signal || null
    const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null
    const cancelled = () => ({ ok: false, cancelled: true, error: 'Upload cancelled.' })
    const isAborted = () => !!(signal && signal.aborted)

    if (!cap) return { ok: false, error: 'File uploads are not enabled for this app.' }
    if (
      !file ||
      typeof file !== 'object' ||
      typeof file.name !== 'string' ||
      typeof file.slice !== 'function'
    ) {
      return { ok: false, error: 'A file is required.' }
    }
    const total = Number(file.size)
    if (!Number.isFinite(total) || total <= 0) return { ok: false, error: 'That file is empty.' }
    const mime = _largeMimeOf(file)
    if (!_largeTypeAllowed(mime, allowedTypes)) {
      return { ok: false, error: 'File type ' + (mime || 'unknown') + ' is not allowed.' }
    }
    if (isAborted()) return cancelled()

    const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
    if (!wsId) return { ok: false, error: 'Open a workspace first.' }

    const sdk = getSdkFn ? getSdkFn() : null
    let fileSvc = null
    try {
      fileSvc =
        sdk && (typeof sdk.getService === 'function' ? await sdk.getService('file') : sdk.file)
    } catch (e) {
      fileSvc = (sdk && sdk.file) || null
    }
    if (!fileSvc || typeof fileSvc._request !== 'function') {
      // An SDK without the raw request (an older one) cannot reach /direct.
      return { ok: false, error: 'File service unavailable.', unavailable: true }
    }
    const call = (path, body, methodName) =>
      fileSvc._request(path, { method: 'POST', body: JSON.stringify(body || {}), methodName })

    // 1. Open the session.
    let session
    try {
      const res = await call(
        '/files/direct',
        {
          originalName: file.name,
          mimeType: mime,
          size: total,
          workspaceId: String(wsId),
          visibility: (cap && cap.visibility) || 'private',
          link: true,
          metadata: {
            workspaceId: String(wsId),
            module: 'workspaceModule',
            kind: options.kind || ''
          }
        },
        'beginDirectUpload'
      )
      session = res && res.data
    } catch (e) {
      // The server's own words are user-facing ("A 3.1 GB file is over the
      // free tier's limit of 50 MB"). The SDK's raw request rejects with the
      // HTTP status on the error. 404 / 405 / 501 here: a server without the
      // direct path (an older API), marked `unavailable`. Only this step
      // marks it: a 404 from storage after the session opened is no older API.
      const status = e && e.status
      return {
        ok: false,
        error: (e && e.message) || 'Could not start the upload.',
        status,
        ...(status === 404 || status === 405 || status === 501 ? { unavailable: true } : {})
      }
    }
    const fileId = session && session.file && (session.file.id || session.file._id)
    const abortSession = async () => {
      if (!fileId) return
      try {
        await call('/files/' + fileId + '/abort', {}, 'abortDirectUpload')
      } catch (e) {
        /* best effort — an unfinished session expires on its own */
      }
    }
    if (!session || !session.uploadUrl || !fileId) {
      await abortSession()
      return { ok: false, error: 'The server did not open an upload.' }
    }
    // An API without the workspace scope ignores it: the upload would land
    // personal (free tier) with no link anyone else could open. Refuse rather
    // than store a file nobody can download.
    if (session.scope !== 'workspace' || !session.link) {
      await abortSession()
      return {
        ok: false,
        error: 'Large uploads are not available on this server yet.',
        unavailable: true
      }
    }

    const quantum = LARGE_CHUNK_QUANTUM
    const asked = Number(session.chunkBytes) || LARGE_DEFAULT_CHUNK_BYTES
    const chunkBytes = Math.max(quantum, Math.floor(asked / quantum) * quantum)

    let lastEmit = 0
    let lastLoaded = -1
    const emit = (loaded, force) => {
      if (!onProgress) return
      const n = Math.min(total, Math.max(0, loaded))
      const t = now()
      if (!force && (n === lastLoaded || t - lastEmit < LARGE_PROGRESS_INTERVAL_MS)) return
      lastEmit = t
      lastLoaded = n
      try {
        onProgress({ loaded: n, total, percent: Math.floor((n / total) * 100) })
      } catch (e) {
        /* a module's progress handler must not break the upload */
      }
    }
    emit(0, true)

    // 2. The bytes.
    let offset = 0
    let attempts = 0
    while (offset < total) {
      if (isAborted()) {
        await abortSession()
        return cancelled()
      }
      const end = Math.min(offset + chunkBytes, total) - 1
      const start = offset
      const r = await _largePut(
        xhrFactory,
        session.uploadUrl,
        file.slice(start, end + 1),
        'bytes ' + start + '-' + end + '/' + total,
        (n) => emit(start + n),
        signal
      )
      if (r.aborted || isAborted()) {
        await abortSession()
        return cancelled()
      }
      if (r.status === 308) {
        // What storage HOLDS, which may be less than was sent.
        const next = _largeNextOffset(r.range)
        offset = next == null ? end + 1 : next
        if (offset > start) {
          attempts = 0
          emit(offset)
          continue
        }
        // Storage took none of it — count it, so a stuck session cannot spin.
        attempts += 1
        if (attempts >= LARGE_MAX_CHUNK_ATTEMPTS) {
          await abortSession()
          return { ok: false, error: 'Storage is not accepting the file. Try again.', status: 308 }
        }
        continue
      }
      if (r.status === 200 || r.status === 201) {
        offset = total
        break
      }
      attempts += 1
      if (!_largeRetryable(r.status) || attempts >= LARGE_MAX_CHUNK_ATTEMPTS) {
        await abortSession()
        return {
          ok: false,
          error:
            r.status === 0
              ? 'The connection dropped and did not come back. Try again.'
              : 'Storage refused the upload (' + r.status + ').',
          status: r.status
        }
      }
      await sleep(Math.min(20000, 1000 * Math.pow(2, attempts - 1)))
      if (isAborted()) {
        await abortSession()
        return cancelled()
      }
      // Ask storage what it holds and resume from there.
      const q = await _largePut(
        xhrFactory,
        session.uploadUrl,
        null,
        'bytes */' + total,
        null,
        signal
      )
      if (q.aborted) {
        await abortSession()
        return cancelled()
      }
      if (q.status === 200 || q.status === 201) {
        offset = total
        break
      }
      if (q.status === 308) offset = _largeNextOffset(q.range) || 0
    }
    emit(total, true)

    // 3. Complete. A 409 means storage has not finalized the object yet.
    let completed = null
    for (let i = 0; i < LARGE_COMPLETE_ATTEMPTS && !completed; i++) {
      try {
        const res = await call('/files/' + fileId + '/complete', {}, 'completeDirectUpload')
        completed = (res && res.data) || {}
      } catch (e) {
        const status = e && e.status
        if (
          (status === 409 || _largeRetryable(Number(status) || 0)) &&
          i < LARGE_COMPLETE_ATTEMPTS - 1
        ) {
          await sleep(1000 * (i + 1))
          continue
        }
        if (status !== 413) await abortSession()
        return { ok: false, error: (e && e.message) || 'Could not finish the upload.', status }
      }
    }
    return {
      ok: true,
      id: fileId,
      url: session.link,
      name: (completed && completed.originalName) || file.name,
      size: (completed && completed.size) || total,
      mimeType: (completed && completed.mimeType) || mime
    }
  }
}

// WRITE-BACK side of the files grant. The other three bridges let a module
// create, list and read; none let it save a change to a document it was HANDED.
// So a `.excalidraw` opened from the /files desktop could be edited and never
// written back — the app's own "save" minted a second document instead, and the
// file you opened quietly stopped being the file you were editing.
//
// The ref is resolved HERE, in the shell, on purpose. There are two file planes
// and they are not interchangeable:
//
//   • the /files desktop — `fileCanvas` rows pointing at `workspaceProject.
//     storage` blobs. This is what `openFileInApp` hands to an app.
//   • the module file plane — Mongo `File` docs via `sdk.file.uploadDocument`,
//     which is what `moduleFileUpload` above writes and `moduleFileList` reads.
//
// A module must not have to know which one it was given, and must not be able
// to aim a write at the other. It hands back the ref it received; the shell
// decides what that names. A ref that resolves to neither is REFUSED by name
// rather than quietly creating a new document somewhere — silently writing to
// the wrong plane is exactly the bug this exists to prevent.
//
// Mirrors the desktop writer in @symbo-ls/workspace-files noteEditor.js
// (`saveFilesNodeMarkdown`) and deliberately does not import it: that package
// is lazily folded on /files, and this bridge has to work from the APP's route,
// where it is not loaded. Same three steps, same ordering guarantee.
// CREATE a document ON the /files desktop — the missing half of the plane
// story above. `moduleFileUpload` writes the module file plane (Mongo `File`
// docs), which the /files desktop never lists — so an app's "Save to Files"
// succeeded, returned ok, and put the document somewhere the user looking at
// /files could not see. Reported as "save to files does not save it"
// (Nika, 2026-08-08), which is exactly what an invisible save looks like.
//
// Same three steps as the desktop's own uploader (files/functions/index.js
// uploadToDesktop) and the write-back bridge below: storage upload →
// fileCanvas row → patch root.fileNodes when the desktop state is loaded.
// Deliberately NOT imported from @symbo-ls/workspace-files — that package is
// lazily folded on /files, and this bridge must work from the APP's route.
//
// Placement is a small scatter near the desktop origin rather than the
// desktop's cell-claiming (`_claimCell` needs the loaded node set, which an
// app route does not have). In practice an app creates ONE node and then
// writes back to it via the ref this returns, so scatter only ever places
// first saves.
//
// Returns `{ ok, node: { id, url, name } }` — the ref shape moduleFileUpdate
// accepts, so the caller can bind to the node and every later save goes BACK
// to it instead of minting siblings.
export const buildModuleFileCreateBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  const cap = (capabilities && capabilities.files) || null
  const allowedTypes =
    Array.isArray(cap && cap.mimeTypes) && cap.mimeTypes.length ? cap.mimeTypes : DEFAULT_MIME_TYPES

  return async function moduleFileCreate(file) {
    if (!file || typeof file !== 'object' || typeof file.name !== 'string') {
      return { ok: false, error: 'A file is required.' }
    }
    if (typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES) {
      return { ok: false, error: 'That file is too large.' }
    }
    const mime = String(file.type || '')
      .toLowerCase()
      .split(';')[0]
      .trim()
    if (mime && allowedTypes.indexOf(mime) < 0) {
      return { ok: false, error: 'File type ' + mime + ' is not allowed.' }
    }

    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk || typeof sdk.execute !== 'function') {
      return { ok: false, error: 'File service unavailable.' }
    }

    try {
      const fd = new FormData()
      fd.append('file', file)
      const up = await sdk.execute('workspaceProject.storage', 'upload', {
        bucket: 'workspace-files',
        formData: fd
      })
      const url = (up && (up.url || (up.data && up.data.url))) || null
      const path = (up && (up.path || (up.data && up.data.path))) || null
      if (!url) return { ok: false, error: 'Storage returned no url.' }

      const payload = {
        kind: 'file',
        name: file.name,
        parent_id: null,
        x: 80 + Math.round(Math.random() * 280),
        y: 80 + Math.round(Math.random() * 180),
        file_id: path,
        url,
        mime_type: mime || null,
        size: typeof file.size === 'number' ? file.size : null,
        scope: (root && root.filesScope) || 'shared',
        created_by: (root && root.currentUserEmail) || null
      }
      const created = await sdk.execute('workspaceProject.fileCanvas', 'create', { payload })
      const id =
        (created && (created.id || created._id)) ||
        (created && created.data && (created.data.id || created.data._id)) ||
        null
      if (!id) return { ok: false, error: 'The Files desktop refused the document.' }

      // When the desktop state is loaded (the user has visited /files this
      // session), show the new tile immediately — same patch shape the
      // desktop's own creators use. When it is not, /files loads the row from
      // the server on its next mount and nothing here is needed.
      if (root && root.update && Array.isArray(root.fileNodes)) {
        root.update({
          fileNodes: [
            ...root.fileNodes,
            {
              id,
              kind: 'file',
              name: payload.name,
              parentId: null,
              x: payload.x,
              y: payload.y,
              fileId: path,
              docId: null,
              url,
              mime: payload.mime_type,
              size: payload.size,
              scope: payload.scope,
              createdBy: payload.created_by,
              createdAt: new Date().toISOString()
            }
          ]
        })
      }
      return { ok: true, node: { id, url, name: payload.name } }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not save to Files.' }
    }
  }
}

export const buildModuleFileUpdateBridge = (
  root,
  capabilities = {},
  { getSdkFn = shellGetSDK } = {}
) => {
  const cap = (capabilities && capabilities.files) || null
  const allowedTypes =
    Array.isArray(cap && cap.mimeTypes) && cap.mimeTypes.length ? cap.mimeTypes : DEFAULT_MIME_TYPES

  return async function moduleFileUpdate(ref, file) {
    if (!file || typeof file !== 'object' || typeof file.name !== 'string') {
      return { ok: false, error: 'A file is required.' }
    }
    if (typeof file.size === 'number' && file.size > MAX_UPLOAD_BYTES) {
      return { ok: false, error: 'That file is too large.' }
    }
    // A grant that cannot upload a type must not be able to write it either.
    const mime = String(file.type || '')
      .toLowerCase()
      .split(';')[0]
      .trim()
    if (mime && allowedTypes.indexOf(mime) < 0) {
      return { ok: false, error: 'File type ' + mime + ' is not allowed.' }
    }

    const nodeId = ref && typeof ref === 'object' ? ref.id : ref
    if (!nodeId) return { ok: false, error: 'A file reference is required.' }
    const node = ((root && root.fileNodes) || []).find((n) => String(n.id) === String(nodeId))
    if (!node) {
      return {
        ok: false,
        error:
          'That file was not opened from this workspace’s Files desktop, so it cannot be updated here.'
      }
    }

    const sdk = getSdkFn ? getSdkFn() : null
    if (!sdk || typeof sdk.execute !== 'function') {
      return { ok: false, error: 'File service unavailable.' }
    }

    // The object this write supersedes. Read BEFORE the upload so a failure
    // anywhere below leaves it exactly where it was.
    const previousPath = node.fileId || null
    try {
      const fd = new FormData()
      fd.append('file', file)
      const up = await sdk.execute('workspaceProject.storage', 'upload', {
        bucket: 'workspace-files',
        formData: fd
      })
      const url = (up && (up.url || (up.data && up.data.url))) || null
      const path = (up && (up.path || (up.data && up.data.path))) || null
      if (!url) return { ok: false, error: 'Storage returned no url.' }

      const size = typeof file.size === 'number' ? file.size : null
      await sdk.execute('workspaceProject.fileCanvas', 'update', {
        id: node.id,
        payload: { url, file_id: path, size }
      })

      // Repoint every reader: the desktop row, and — for a text format — the
      // tile snippet cache keyed by node id, which otherwise keeps showing the
      // previous revision until a reload. The snippet is only rewritten when we
      // can actually produce one; blanking it instead would leave the tile empty
      // for good, since the loader only fills a snippet from the tile's
      // onCreate and these tiles are already mounted.
      const patch = {
        fileNodes: (root.fileNodes || []).map((n) =>
          String(n.id) === String(node.id) ? { ...n, url, fileId: path, size } : n
        )
      }
      if (mime.indexOf('text/') === 0 && typeof file.text === 'function') {
        try {
          const text = await file.text()
          patch._filePreviews = {
            ...((root && root._filePreviews) || {}),
            [node.id]: text.slice(0, 400)
          }
        } catch (readErr) {
          /* snippet is a nicety; a save must not fail over one */
        }
      }
      if (root && typeof root.update === 'function') root.update(patch, { preventFetch: true })

      // Reclaim the replaced object — AFTER the row points at the new one, never
      // before, or a failed update would leave the row naming an object that no
      // longer exists. Best-effort: a failed delete is an orphan, which is what
      // we had before, so it must never fail the save.
      if (previousPath && previousPath !== path) {
        try {
          await sdk.execute('workspaceProject.storage', 'remove', {
            bucket: 'workspace-files',
            path: previousPath
          })
        } catch (rmErr) {
          console.warn('[moduleFileUpdate] superseded blob not removed:', rmErr && rmErr.message)
        }
      }
      return { ok: true, id: node.id, url, size }
    } catch (e) {
      return { ok: false, error: (e && e.message) || 'Could not save the file.' }
    }
  }
}
